import cn from 'classnames';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { i18nPath } from 'config/i18nNs';
import useTabs from 'hooks/useTabs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { projectId } from 'utils/urlQueries';
import { getPathInfo } from 'utils/versionUtils';
import styles from './sideNav.module.scss';

type DrilldownType = 'activities' | 'cookies';

interface DrilldownItem {
  label?: string;
  tabId?: string;
  anchor?: string;
  [key: string]: any;
}

export interface SideNavProps {
  navItems: Array<{
    label: string;
    icon?: string;
    anchor: string;
    listActivities?: boolean;
    listCookies?: boolean;
  }>;
  activities: Array<{ [key: string]: string }>;
  cookieTypes: Array<{ [key: string]: string }>;
  isEmployees: boolean;
  pageAlias: string;
}

const SideNav: React.FC<SideNavProps> = ({
  navItems,
  activities,
  cookieTypes,
  isEmployees,
  pageAlias
}) => {
  const { t } = useTranslation();
  const { locPath } = getPathInfo();
  const history = useHistory();
  const location = useLocation();
  const { activeTab, isTabsVisible, setActiveTab, showTabs } = useTabs();
  const [drilldownType, setDrilldownType] =
    React.useState<DrilldownType | null>(null);
  const currentProjectId =
    new URLSearchParams(location.search).get('id') || projectId;
  const defaultSearch = currentProjectId ? `?id=${currentProjectId}` : '';
  const pagePath = `/${locPath}/${pageAlias}`;
  const pageURL = `${pagePath}${defaultSearch}`;
  const pathAfterPage = location.pathname
    .replace(new RegExp(`^/${locPath}/${pageAlias}/?`), '')
    .replace(/^\/+|\/+$/g, '');
  const drilldownItemFromPath = pathAfterPage.split('/')[0] || '';

  const clickLink = (buttonId: string) => {
    showTabs(false);
    document.getElementById(`button-${buttonId}`)?.focus();
  };

  const focusTabButton = (buttonId: string) => {
    document.getElementById(`button-${buttonId}`)?.focus();
  };

  const hasActivities = navItems?.some((item) => item.listActivities);
  const hasCookies = navItems?.some((item) => item.listCookies);
  const routeDrilldownType: DrilldownType | null =
    hasActivities &&
    Array.isArray(activities) &&
    activities.some(
      (item: DrilldownItem) => item?.tabId === drilldownItemFromPath
    )
      ? 'activities'
      : hasCookies &&
        Array.isArray(cookieTypes) &&
        cookieTypes.some(
          (item: DrilldownItem) => item?.tabId === drilldownItemFromPath
        )
      ? 'cookies'
      : null;
  const isRouteBackedDrilldown = Boolean(
    routeDrilldownType && drilldownItemFromPath
  );

  React.useEffect(() => {
    if (routeDrilldownType) {
      setDrilldownType(routeDrilldownType);
      return;
    }

    setDrilldownType(null);
  }, [location.pathname, routeDrilldownType]);

  React.useEffect(() => {
    if (!isTabsVisible || !activeTab) {
      return;
    }

    if (
      hasActivities &&
      Array.isArray(activities) &&
      activities.some((item: DrilldownItem) => item?.tabId === activeTab)
    ) {
      setDrilldownType('activities');
      return;
    }

    if (
      hasCookies &&
      Array.isArray(cookieTypes) &&
      cookieTypes.some((item: DrilldownItem) => item?.tabId === activeTab)
    ) {
      setDrilldownType('cookies');
    }
  }, [
    activeTab,
    activities,
    cookieTypes,
    hasActivities,
    hasCookies,
    isTabsVisible
  ]);

  const closeDrilldown = () => {
    setDrilldownType(null);
    showTabs(false);
  };

  const goBackFromDrilldown = () => {
    if (isRouteBackedDrilldown) {
      history.push(pageURL);
    }

    closeDrilldown();
  };

  const getDrilldownURL = (tabId: string) =>
    `/${locPath}/${pageAlias}/${tabId}${defaultSearch}`;

  const handleDrilldownClick = (item: DrilldownItem, e?: React.MouseEvent) => {
    if (!item?.tabId) {
      return;
    }

    if (isRouteBackedDrilldown) {
      e?.preventDefault();
      history.push(getDrilldownURL(item.tabId));
      focusTabButton(item.tabId);
      return;
    }

    setActiveTab(item.tabId);
  };

  const handleDrilldownKeyDown = (
    item: DrilldownItem,
    e: React.KeyboardEvent
  ) => {
    if (e.key !== 'Enter') {
      return;
    }

    handleDrilldownClick(item);
  };

  const drilldownItems =
    drilldownType === 'activities' ? activities : cookieTypes;

  if (drilldownType) {
    return (
      <div className={styles.root}>
        <nav>
          <ul className={cn(styles.menu, styles.drilldownMenu)}>
            <li className={styles.backItem}>
              <Button
                type="button"
                onClick={goBackFromDrilldown}
                variant="secondary"
                size="small"
              >
                <Icon name="icon-arrow-left" size={16} />
                {t(`${i18nPath.dictionary}.back`, 'Back')}
              </Button>
            </li>

            {drilldownItems &&
              Array.isArray(drilldownItems) &&
              drilldownItems.map((item: DrilldownItem, index) => {
                const isActive = isRouteBackedDrilldown
                  ? drilldownItemFromPath === item.tabId
                  : activeTab === item.tabId;

                return (
                  <li
                    key={`drilldownItem${index}`}
                    className={styles.drilldownItem}
                    tabIndex={0}
                    onKeyDown={(e) => handleDrilldownKeyDown(item, e)}
                  >
                    <a
                      className={cn(styles.drilldownLink, {
                        [styles.linkActive]: isActive
                      })}
                      href={
                        isRouteBackedDrilldown && item.tabId
                          ? getDrilldownURL(item.tabId)
                          : item.anchor || '#'
                      }
                      onClick={(e) => handleDrilldownClick(item, e)}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
          </ul>
        </nav>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <nav>
        <ul className={styles.menu}>
          {navItems &&
            navItems.map((item, index) => {
              const anchorHash = item.anchor.startsWith('#')
                ? item.anchor
                : `#${item.anchor}`;
              const anchorURL = `${pageURL}${anchorHash}`;
              return (
                <li key={`nav-${index}`}>
                  {item.icon && <Icon name={item.icon} />}
                  <NavLink
                    to={`${anchorURL}`}
                    onClick={() => clickLink(item.anchor)}
                  >
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
        </ul>
      </nav>
    </div>
  );
};

export default SideNav;
